import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiBriefcase, FiCheck } from 'react-icons/fi';

interface ServiceFilterModalProps {
  services: string[];
  selectedService: string;
  onSelectService: (service: string) => void;
  onClose: () => void;
}

export const ServiceFilterModal: React.FC<ServiceFilterModalProps> = ({
  services,
  selectedService,
  onSelectService,
  onClose,
}) => {
  const [search, setSearch] = useState('');

  const filteredServices = services.filter((s) => s.toLowerCase().includes(search.toLowerCase()));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -6 }}
      transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      className="absolute top-full mt-2.5 z-40 w-[260px] -right-12 sm:right-0 sm:w-[300px] rounded-3xl border border-[#3A3A3C] bg-[#1C1C1E] p-3 shadow-[0_20px_50px_rgba(0,0,0,0.85)] backdrop-blur-xl"
    >
      {/* Search Input */}
      <div className="relative mb-3">
        <FiSearch
          size={16}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
        />
        <input
          type="text"
          placeholder="My Services"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-2xl border border-[#3A3A3C] bg-[#2A2A2D] py-2.5 pl-10 pr-3 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-[#48484A]"
        />
      </div>

      {/* Services List with no-scrollbar */}
      <div className="max-h-64 space-y-1.5 overflow-y-auto no-scrollbar pr-1">
        <button
          onClick={() => {
            onSelectService('All');
            onClose();
          }}
          className={`flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E] p-3 text-left transition ${
            selectedService === 'All'
              ? 'bg-[#3A3A3C] border-[#48484A]'
              : 'bg-[#2A2A2D] hover:bg-[#3A3A3C]'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300">
              <FiBriefcase size={15} />
            </div>
            <span className="text-sm font-medium text-white truncate">All</span>
          </div>
          {selectedService === 'All' && (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00D45A] text-black">
              <FiCheck size={14} className="stroke-[3]" />
            </div>
          )}
        </button>

        {filteredServices.map((service) => {
          const isSelected = selectedService === service;
          return (
            <button
              key={service}
              onClick={() => {
                onSelectService(service);
                onClose();
              }}
              className={`flex w-full items-center justify-between rounded-2xl border border-[#2C2C2E] p-3 text-left transition ${
                isSelected ? 'bg-[#3A3A3C] border-[#48484A]' : 'bg-[#2A2A2D] hover:bg-[#3A3A3C]'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300">
                  <FiBriefcase size={15} />
                </div>
                <span className="text-sm font-medium text-white truncate">{service}</span>
              </div>
              {isSelected && (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00D45A] text-black">
                  <FiCheck size={14} className="stroke-[3]" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};
